import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Select from '../../../components/ui/Select';

const KYCReview = () => {
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(0);
  const [annotations, setAnnotations] = useState([]);
  const [reviewStatus, setReviewStatus] = useState('pending');

  const pendingReviews = [
  {
    id: 1,
    name: "Sarah Johnson",
    businessName: "Johnson\'s Catering Services",
    avatar: "https://images.unsplash.com/photo-1684262855358-88f296a2cfc2",
    avatarAlt: "Professional headshot of woman with brown hair in white blazer smiling",
    vertical: "Food",
    submittedAt: "2025-10-27T10:30:00Z",
    priority: "high",
    documents: [
    {
      type: "Business License",
      url: "https://images.unsplash.com/photo-1724933444870-4e1edb665fd5",
      alt: "Business license document with official seal and registration details",
      status: "pending",
      notes: ""
    },
    {
      type: "Food Safety Certificate",
      url: "https://images.unsplash.com/photo-1619993892426-35116e34a7db",
      alt: "Food safety certificate showing compliance with health regulations",
      status: "pending",
      notes: ""
    },
    {
      type: "Insurance Certificate",
      url: "https://images.unsplash.com/photo-1641748182997-f9745e9a0348",
      alt: "Insurance certificate document showing coverage details and policy information",
      status: "pending",
      notes: ""
    }]

  },
  {
    id: 2,
    name: "Michael Chen",
    businessName: "Chen Home Services",
    avatar: "https://images.unsplash.com/photo-1659323593305-ad05297db6a2",
    avatarAlt: "Professional headshot of Asian man in navy suit with confident smile",
    vertical: "Services",
    submittedAt: "2025-10-26T14:20:00Z",
    priority: "medium",
    documents: [
    {
      type: "ID Verification",
      url: "https://images.unsplash.com/photo-1579531457888-fd0e76affb69",
      alt: "Government issued ID card with photo and personal information",
      status: "approved",
      notes: "Valid ID, all information matches"
    },
    {
      type: "Professional License",
      url: "https://images.unsplash.com/photo-1415215679275-35dbb60076c8",
      alt: "Professional service license certificate with registration number",
      status: "pending",
      notes: ""
    }]

  }];


  const statusOptions = [
  { value: 'pending', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'needs_clarification', label: 'Needs Clarification' }];


  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':return 'bg-red-100 text-red-800';
      case 'medium':return 'bg-amber-100 text-amber-800';
      case 'low':return 'bg-green-100 text-green-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':return 'bg-green-100 text-green-800';
      case 'rejected':return 'bg-red-100 text-red-800';
      case 'needs_clarification':return 'bg-amber-100 text-amber-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const submitted = new Date(dateString);
    const diffInHours = Math.floor((now - submitted) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  const handlePartnerSelect = (partner) => {
    setSelectedPartner(partner);
    setSelectedDocument(0);
    setAnnotations([]);
  };

  const handleDocumentApproval = (action) => {
    if (!selectedPartner) return;

    const updatedDocuments = [...selectedPartner?.documents];
    updatedDocuments[selectedDocument].status = action;

    // In a real app, this would update the backend
    // console.log(`Document ${action}:`, updatedDocuments?.[selectedDocument]);
  };

  const handleBulkAction = (action) => {
    if (!selectedPartner) return;

    // In a real app, this would update all documents
    // console.log(`Bulk ${action} for partner:`, selectedPartner?.name);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
      {/* Partner List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Pending KYC Reviews</h3>
            <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
              {pendingReviews?.length}
            </span>
          </div>
        </div>

        <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
          {pendingReviews?.map((partner) =>
          <div
            key={partner?.id}
            onClick={() => handlePartnerSelect(partner)}
            className={`p-4 rounded-lg border cursor-pointer transition-all hover:elevation-1 ${
            selectedPartner?.id === partner?.id ?
            'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`
            }>

              <div className="flex items-start space-x-3">
                <Image
                src={partner?.avatar}
                alt={partner?.avatarAlt}
                className="w-12 h-12 rounded-full object-cover" />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-foreground truncate">{partner?.name}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(partner?.priority)}`}>
                      {partner?.priority}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-2 truncate">
                    {partner?.businessName}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {formatTimeAgo(partner?.submittedAt)}
                    </span>
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">
                      {partner?.documents?.length} docs
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Document Viewer */}
      <div className="lg:col-span-2 space-y-6">
        {selectedPartner ?
        <>
            {/* Partner Info Header */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Image
                  src={selectedPartner?.avatar}
                  alt={selectedPartner?.avatarAlt}
                  className="w-16 h-16 rounded-full object-cover" />

                  <div>
                    <h2 className="text-xl font-semibold text-foreground">{selectedPartner?.name}</h2>
                    <p className="text-muted-foreground">{selectedPartner?.businessName}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {selectedPartner?.vertical}
                      </span>
                      <span className={`text-sm px-2 py-1 rounded ${getPriorityColor(selectedPartner?.priority)}`}>
                        {selectedPartner?.priority} priority
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button variant="outline" onClick={() => handleBulkAction('approve')}>
                    <Icon name="CheckCircle" size={16} className="mr-2" />
                    Approve All
                  </Button>
                  <Button variant="destructive" onClick={() => handleBulkAction('reject')}>
                    <Icon name="XCircle" size={16} className="mr-2" />
                    Reject All
                  </Button>
                </div>
              </div>
            </div>

            {/* Document Tabs */}
            <div className="bg-card border border-border rounded-lg">
              <div className="border-b border-border">
                <div className="flex space-x-1 p-4">
                  {selectedPartner?.documents?.map((doc, index) =>
                <button
                  key={index}
                  onClick={() => setSelectedDocument(index)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedDocument === index ?
                  'bg-primary text-primary-foreground' :
                  'text-muted-foreground hover:text-foreground hover:bg-muted'}`
                  }>

                      <div className="flex items-center space-x-2">
                        <span>{doc?.type}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(doc?.status)}`}>
                          {doc?.status}
                        </span>
                      </div>
                    </button>
                )}
                </div>
              </div>

              {/* Document Content */}
              <div className="p-6">
                {selectedPartner?.documents?.[selectedDocument] &&
              <div className="space-y-6">
                    {/* Document Image */}
                    <div className="bg-muted rounded-lg p-4">
                      <Image
                    src={selectedPartner?.documents?.[selectedDocument]?.url}
                    alt={selectedPartner?.documents?.[selectedDocument]?.alt}
                    className="w-full h-96 object-contain rounded-lg" />

                    </div>

                    {/* Document Actions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Status and Notes */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Review Status
                          </label>
                          <Select
                        options={statusOptions}
                        value={selectedPartner?.documents?.[selectedDocument]?.status}
                        onChange={(value) => {
                          const updatedDocuments = [...selectedPartner?.documents];
                          updatedDocuments[selectedDocument].status = value;
                          setSelectedPartner({
                            ...selectedPartner,
                            documents: updatedDocuments
                          });
                        }} />

                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Review Notes
                          </label>
                          <textarea
                        className="w-full p-3 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                        rows={4}
                        placeholder="Add notes about this document..."
                        value={selectedPartner?.documents?.[selectedDocument]?.notes}
                        onChange={(e) => {
                          const updatedDocuments = [...selectedPartner?.documents];
                          updatedDocuments[selectedDocument].notes = e?.target?.value;
                          setSelectedPartner({
                            ...selectedPartner,
                            documents: updatedDocuments
                          });
                        }} />

                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-foreground">Quick Actions</h4>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <Button
                        variant="success"
                        onClick={() => handleDocumentApproval('approved')}
                        className="w-full">

                            <Icon name="Check" size={16} className="mr-2" />
                            Approve
                          </Button>
                          
                          <Button
                        variant="destructive"
                        onClick={() => handleDocumentApproval('rejected')}
                        className="w-full">

                            <Icon name="X" size={16} className="mr-2" />
                            Reject
                          </Button>
                          
                          <Button
                        variant="warning"
                        onClick={() => handleDocumentApproval('needs_clarification')}
                        className="w-full">

                            <Icon name="AlertCircle" size={16} className="mr-2" />
                            Need Info
                          </Button>
                          
                          <Button variant="outline" className="w-full">
                            <Icon name="Download" size={16} className="mr-2" />
                            Download
                          </Button>
                        </div>

                        <div className="pt-4 border-t border-border">
                          <Button variant="ghost" className="w-full">
                            <Icon name="MessageSquare" size={16} className="mr-2" />
                            Contact Partner
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
              }
              </div>
            </div>
          </> :

        <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Select a Partner</h3>
            <p className="text-muted-foreground">
              Choose a partner from the list to review their KYC documents
            </p>
          </div>
        }
      </div>
    </div>);

};

export default KYCReview;